import {
  Check,
  CloudUpload,
  type LucideIcon,
  Mic,
  RefreshCw,
  RotateCcw,
  Square,
  Upload,
} from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { SHEET_PRIMARY_ICON, SheetHeader } from '../check/SheetChrome'
import { MODAL_FOOTER, MODAL_PRIMARY, TEXT_CAPTION } from '../recipes'
import { SubjectLabel } from '../SubjectLabel'
import { VoiceFileGuide, VoiceFilePicker } from './VoiceFilePicker'
import { VoiceInstrument } from './VoiceInstrument'
import { VoiceRail } from './VoiceRail'
import { VOICE_SAMPLE_TARGET_SPEECH_MS, voiceTopicAt } from './voice-rules'
import { mayRecordBetter } from './voice-sample-quality'
import {
  IDLE_VOICE_READOUT,
  type VoicePlayer,
  type VoiceReadout,
  type VoiceSampleActions,
  type VoiceSampleContext,
  type VoiceSampleMode,
  type VoiceSampleView,
} from './voice-types'

export interface VoiceSamplePanelProps {
  view: VoiceSampleView
  /** What the sample is for, and whose voice it is (none before the subject exists). */
  context: VoiceSampleContext
  /** What moves: the counted speech while it records, the player on the recorded sample. */
  live?: { readout?: VoiceReadout; player?: VoicePlayer | null }
  actions: VoiceSampleActions
}

/** Id of the panel title, for `aria-labelledby`. */
export const VOICE_SAMPLE_TITLE_ID = 'voice-sample-title'

const COPY = 'humint.voice.sample'

/** One button of the footer; `label` is its i18n key. */
interface FooterButton {
  label: string
  onClick?: () => void
  Icon?: LucideIcon
  loading?: boolean
  /** The icon is a filled shape (the stop square). */
  filled?: boolean
  /** The phase's one copper button. */
  primary?: boolean
}

/** The footer's buttons in the order they are drawn: two at most. */
type FooterButtons = readonly FooterButton[]

const action = (name: string): string => `${COPY}.actions.${name}`
const CLOSE = 'humint.common.actions.close'
const DONE = 'humint.common.actions.done'

/** *Record again* where a sample was dropped or cannot be kept; nothing where it is not offered. */
const again = (actions: VoiceSampleActions, primary = false): FooterButtons =>
  actions.onRecordAgain
    ? [{ label: action('recordAgain'), onClick: actions.onRecordAgain, Icon: RotateCcw, primary }]
    : []

/**
 * The review: keep the sample, or record again; with too little speech only the latter. Keeping
 * comes first, so it never stands where *Stop* stood a moment ago — the second tap of a hurried
 * double tap lands on *Record again*, which asks before it drops anything, and a sample is kept
 * only by a tap that was aimed at keeping it.
 */
function reviewButtons(mode: VoiceSampleMode, actions: VoiceSampleActions): FooterButtons {
  if (!actions.onSave) return again(actions, true)
  const keep: FooterButton =
    mode === 'collect'
      ? { label: action('use'), onClick: actions.onSave, Icon: Check, primary: true }
      : { label: action('save'), onClick: actions.onSave, Icon: CloudUpload, primary: true }
  return [keep, ...again(actions)]
}

/**
 * A sample that failed: a way out, then the same one again where that may help, else another.
 */
function failedButtons(actions: VoiceSampleActions): FooterButtons {
  const close: FooterButton = { label: CLOSE, onClick: actions.onClose }
  if (!actions.onRetry) return [close, ...again(actions, true)]
  const retry = 'humint.common.actions.retry'
  return [close, { label: retry, onClick: actions.onRetry, Icon: RefreshCw, primary: true }]
}

/**
 * While the room is checked there is nothing to tap: the one button waits. Where the room was
 * found too loud: record all the same, or — the copper one — check again, elsewhere.
 */
function roomButtons(
  view: Extract<VoiceSampleView, { phase: 'room' }>,
  actions: VoiceSampleActions
): FooterButtons {
  if (view.check === 'measuring') {
    return [{ label: action('measuring'), loading: true, primary: true }]
  }
  const { onGoOn, onStartOver } = actions
  const goOn: FooterButtons = onGoOn ? [{ label: action('goOn'), onClick: onGoOn, Icon: Mic }] : []
  const again: FooterButtons = onStartOver
    ? [{ label: action('checkAgain'), onClick: onStartOver, Icon: RotateCcw, primary: true }]
    : []
  return [...goOn, ...again]
}

/**
 * A kept voiceprint: *Done* — and, where the server's verdict says another recording could give
 * a better sample, *Record again* beside it. After a weak sample that is the way on, as after too
 * little speech.
 */
function doneButtons(
  view: Extract<VoiceSampleView, { phase: 'done' }>,
  actions: VoiceSampleActions
): FooterButtons {
  const better = mayRecordBetter(view.quality) ? again(actions) : []
  if (view.quality?.grade === 'low' && better.length > 0) {
    return [{ label: CLOSE, onClick: actions.onClose }, ...again(actions, true)]
  }
  return [{ label: DONE, onClick: actions.onClose, primary: true }, ...better]
}

/** While it records: start over, then the one copper button that ends it. */
function recordingButtons(actions: VoiceSampleActions): FooterButtons {
  const { onStartOver, onStop } = actions
  const startOver: FooterButtons = onStartOver
    ? [{ label: action('startOver'), onClick: onStartOver, Icon: RotateCcw }]
    : []
  const stop: FooterButtons = onStop
    ? [{ label: action('stop'), onClick: onStop, Icon: Square, filled: true, primary: true }]
    : []
  return [...startOver, ...stop]
}

/**
 * The footer of each phase: one copper action in the thumb zone and, where there is one, a
 * second way beside it. A control whose callback the host did not pass is not drawn.
 */
function footerButtons(
  view: VoiceSampleView,
  mode: VoiceSampleMode,
  actions: VoiceSampleActions,
  onChooseFile: () => void
): FooterButtons {
  const close: FooterButton = { label: CLOSE, onClick: actions.onClose }
  switch (view.phase) {
    case 'instructions': {
      const upload: FooterButtons =
        actions.onImportFile && !view.starting
          ? [{ label: `${COPY}.file.upload`, onClick: onChooseFile, Icon: Upload }]
          : []
      if (!actions.onStart) return [...upload, close]
      const { starting: loading } = view
      return [
        ...upload,
        { label: action('record'), onClick: actions.onStart, Icon: Mic, loading, primary: true },
      ]
    }
    case 'room':
      return roomButtons(view, actions)
    case 'recording':
      return recordingButtons(actions)
    case 'review':
      return reviewButtons(mode, actions)
    case 'saving':
      return [{ label: action('saving'), loading: true, primary: true }]
    case 'processing':
      return [{ label: DONE, onClick: actions.onClose, primary: true }]
    case 'done':
      return doneButtons(view, actions)
    case 'no_speech':
      // The same sample again would hold as little speech: only another one helps.
      return [close, ...again(actions, true)]
    case 'failed':
      return failedButtons(actions)
  }
}

/** The second way beside the copper button: the sheets' outlined button. */
const FOOTER_SECONDARY =
  'w-full rounded-xl text-base font-semibold @3xl/modal:h-10 @3xl/modal:w-auto @3xl/modal:px-4 @3xl/modal:text-sm'

/** A button as half of the phone's footer row: its words on two lines where one will not hold them. */
const FOOTER_HALF = 'min-w-0 gap-2 px-3 leading-tight whitespace-normal text-balance'

/**
 * One button of the footer. On the phone the footer is one row whatever the phase, so the
 * instrument over it keeps one height: a button is half of the row, or all of it alone. On the
 * desktop the buttons keep to the end of the row at their own width.
 */
function FooterAction({ button, sole = false }: { button: FooterButton; sole?: boolean }) {
  const { t } = useTranslation()
  const { label, onClick, Icon, loading, filled, primary } = button
  return (
    <Button
      type="button"
      variant={primary ? 'solid' : 'outline'}
      size="2xl"
      className={cn(primary ? MODAL_PRIMARY : FOOTER_SECONDARY, FOOTER_HALF, sole && 'col-span-2')}
      onClick={onClick}
      loading={loading}
    >
      {Icon && !loading && (
        <Icon aria-hidden className={cn(SHEET_PRIMARY_ICON, filled && 'fill-current')} />
      )}
      {t(label)}
    </Button>
  )
}

function PanelFooter({
  view,
  context,
  actions,
  onChooseFile,
}: Omit<VoiceSamplePanelProps, 'live'> & { onChooseFile: () => void }) {
  const buttons = footerButtons(view, context.mode, actions, onChooseFile).map(button =>
    'take' in view && view.take.fileName && button.label === action('recordAgain')
      ? { ...button, label: `${COPY}.file.chooseAnother` }
      : button
  )
  const [first, last] = buttons
  return (
    <footer className={MODAL_FOOTER}>
      <div className="grid grid-cols-2 gap-2 @3xl/modal:contents">
        {first && <FooterAction button={first} sole={!last} />}
        {last && <FooterAction button={last} />}
      </div>
    </footer>
  )
}

function PanelHeader({ view, context, actions }: Omit<VoiceSamplePanelProps, 'live'>) {
  const { t } = useTranslation()
  const { subject } = context
  // What the sample is for takes two lines on a phone: it is read on the instructions, and gives
  // its second line to the instrument after them.
  const brief = !subject && view.phase !== 'instructions'
  return (
    // The sample on its way cannot be left: nothing would say how it went.
    <SheetHeader closable={view.phase !== 'saving'} onClose={actions.onClose}>
      {/* As tall as the close beside it, so the header keeps its height while that is hidden. */}
      <div className="flex min-h-12 min-w-0 flex-1 flex-col gap-0.5 @3xl/modal:min-h-10">
        <h2
          id={VOICE_SAMPLE_TITLE_ID}
          className="text-xl leading-[26px] font-semibold @3xl/modal:text-lg"
        >
          {t(`${COPY}.title`)}
        </h2>
        <p
          className={cn(
            TEXT_CAPTION,
            'min-w-0 text-muted-foreground',
            brief ? 'hidden @3xl/modal:flex' : 'flex'
          )}
        >
          {subject ? (
            <SubjectLabel subject={subject} />
          ) : (
            t(`${COPY}.purpose`, { seconds: VOICE_SAMPLE_TARGET_SPEECH_MS / 1000 })
          )}
        </p>
      </div>
    </SheetHeader>
  )
}

/**
 * The voice-sample dialog's panel (ADR-0062), the same for a subject being enrolled and one
 * already enrolled: what the subject is asked to do, the check of the room, the speech ring that
 * moves only while their voice is heard, the recorded sample to listen to, how saving it went,
 * and the server's verdict on the stored sample. Phone: one column in
 * a tall sheet — the instructions first, then the instrument in their place, the actions in one
 * row in the thumb zone. Desktop (from 48 rem of panel): the instructions as a numbered rail on the
 * left, the instrument on its display on the right, in a dialog that keeps one size through every
 * phase. Pure: the host owns the microphone and the upload.
 */
export function VoiceSamplePanel({ view, context, live, actions }: VoiceSamplePanelProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const readout = live?.readout ?? IDLE_VOICE_READOUT
  const intro = view.phase === 'instructions'
  const imported = 'take' in view && Boolean(view.take.fileName)
  return (
    <section
      aria-labelledby={VOICE_SAMPLE_TITLE_ID}
      className="@container/modal flex min-h-0 flex-1 flex-col"
    >
      <PanelHeader view={view} context={context} actions={actions} />
      <div className="flex min-h-0 flex-1 flex-col @3xl/modal:grid @3xl/modal:grid-cols-[minmax(0,5fr)_minmax(0,8fr)] @3xl/modal:gap-6 @3xl/modal:px-6 @3xl/modal:pb-5">
        <div
          // On a short phone the last topics scroll: the lower edge fades, as the instrument's does.
          className={cn(
            'min-h-0 flex-1 overflow-y-auto px-4 pt-1 pb-6 @max-3xl/modal:[mask-image:linear-gradient(to_bottom,black_calc(100%-1.5rem),transparent)] @3xl/modal:block @3xl/modal:px-0 @3xl/modal:pb-0',
            !intro && 'hidden'
          )}
        >
          {intro && actions.onImportFile && (
            <VoiceFilePicker onFile={actions.onImportFile} inputRef={fileInput} />
          )}
          {imported ? (
            <VoiceFileGuide />
          ) : (
            <VoiceRail
              problem={intro ? view.problem : null}
              activeTopic={view.phase === 'recording' ? voiceTopicAt(readout.talkMs) : null}
              quiet={!intro && view.phase !== 'recording' && view.phase !== 'room'}
            />
          )}
        </div>
        <div className={cn('min-h-0 flex-1 flex-col @3xl/modal:flex', intro ? 'hidden' : 'flex')}>
          <VoiceInstrument view={view} context={context} readout={readout} player={live?.player} />
        </div>
      </div>
      <PanelFooter
        view={view}
        context={context}
        actions={actions}
        onChooseFile={() => fileInput.current?.click()}
      />
    </section>
  )
}
